import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { map, Observable, take } from 'rxjs';
import { AuthState } from '../store/auth/auth.state';
import { AppSelectors } from '../store/app/app.selectors';

/**
 * Custom authentication guard that preserves the return URL when redirecting to log in.
 * This guard wraps the @bo/ng-auth AuthenticatedGuard functionality but adds returnUrl support.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticatedWithReturnUrlGuard {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  canActivate(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.store.select(AuthState.token).pipe(
      take(1), // CRITICAL: Complete the observable after the first emission
      map((token) => {
        // Check if the session is already expired (modal is showing)
        // If so, allow navigation to prevent race conditions
        const sessionExpired = this.store.selectSnapshot(AppSelectors.sessionExpired);
        if (sessionExpired) {
          // Don't redirect to log in, let the session expired modal handle it
          return true;
        }

        // Check if token exists
        // NOTE: We allow expired tokens through because the interceptor will
        // attempt to refresh them on the first API request. This prevents
        // redirecting to log in when the refresh would succeed.
        if (!token) {
          // No token at all, redirect to log in with returnUrl
          return this.router.createUrlTree(['/login'], {
            queryParams: { returnUrl: state.url },
          });
        }

        // Token exists (maybe expired), allow navigation
        // The interceptor will handle refresh if needed
        return true;
      }),
    );
  }
}

/**
 * Functional guard for use in route configuration
 */
export const canActivateWithReturnUrl: CanActivateFn = (_route, state): Observable<boolean | UrlTree> => {
  return inject(AuthenticatedWithReturnUrlGuard).canActivate(state);
};
